export const APPROVAL_QUEUE_PAGE_SIZE = 200;

const PENDING_ORDER_STATUSES = new Set(["pending", "payment pending"]);

export const normalizeOrderStatus = (status) =>
  String(status ?? "").trim().toLowerCase();

export const isPrepaidOrder = (order) => order?.PrepaidOrder === true;

export const isPrepaidDirectOrder = (order) =>
  isPrepaidOrder(order) &&
  (order?.OrderType === "Direct Order" || order?.OrderType === "DO");

export const isPendingOrder = (order) =>
  PENDING_ORDER_STATUSES.has(normalizeOrderStatus(order?.Status));

export const isApprovedOrder = (order) =>
  normalizeOrderStatus(order?.Status) === "approved";

const getOrderIdentity = (order, fallbackKey) => {
  if (order?.ID === null || order?.ID === undefined) {
    return fallbackKey;
  }

  return `DirectOrder:${order.ID}`;
};

const getOrderTimestamp = (order) => {
  const timestamp = new Date(order?.OrderDate ?? "").getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

const mergePrepaidDirectOrders = (
  pendingDirectOrders,
  approvedDirectOrders
) => {
  const byIdentity = new Map();

  pendingDirectOrders
    .filter((order) => isPrepaidDirectOrder(order) && isPendingOrder(order))
    .forEach((order, index) => {
      byIdentity.set(getOrderIdentity(order, `pending:${index}`), order);
    });

  // Approved data is applied second so it wins if an order changes status
  // between the two parallel API requests.
  approvedDirectOrders
    .filter((order) => isPrepaidDirectOrder(order) && isApprovedOrder(order))
    .forEach((order, index) => {
      byIdentity.set(getOrderIdentity(order, `approved:${index}`), order);
    });

  return Array.from(byIdentity.values()).sort(
    (left, right) => getOrderTimestamp(right) - getOrderTimestamp(left)
  );
};

export const buildApprovalQueues = ({
  productUsageOrders = [],
  consignmentOrders = [],
  pendingDirectOrders = [],
  approvedDirectOrders = [],
} = {}) => {
  const standardDirectOrders = pendingDirectOrders.filter(
    (order) => !isPrepaidOrder(order) && isPendingOrder(order)
  );

  return {
    standard: [
      ...consignmentOrders,
      ...standardDirectOrders,
      ...productUsageOrders,
    ],
    prepaid: mergePrepaidDirectOrders(
      pendingDirectOrders,
      approvedDirectOrders
    ),
  };
};

export const getApprovalRowAction = ({
  order,
  approvalTabType,
  canManageOrder,
}) => {
  if (approvalTabType === "prepaid") {
    if (!isPrepaidDirectOrder(order)) {
      return null;
    }

    if (isApprovedOrder(order)) {
      return canManageOrder
        ? { kind: "fulfill", label: "Fulfill Order", type: "success" }
        : null;
    }

    return {
      kind: "view",
      label: "View",
      type: "warning",
      path: `/DirectOrder/${order.ID}`,
    };
  }

  if (isPrepaidOrder(order)) {
    return null;
  }

  return canManageOrder
    ? { kind: "approve", label: "Approve", type: "success" }
    : null;
};

const appendPagination = (endpoint, page) => {
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}page=${page}&pageSize=${APPROVAL_QUEUE_PAGE_SIZE}`;
};

export const fetchAllApprovalQueuePages = async (apiService, endpoint) => {
  const firstResponse = await apiService.getDataFromBackend(
    appendPagination(endpoint, 1)
  );
  const firstRows = firstResponse?.data?.data || [];
  const totalPagesValue = Number(firstResponse?.data?.totalPages ?? 1);
  const totalPages =
    Number.isInteger(totalPagesValue) && totalPagesValue > 0
      ? totalPagesValue
      : 1;

  if (totalPages === 1) {
    return firstRows;
  }

  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      apiService.getDataFromBackend(appendPagination(endpoint, index + 2))
    )
  );

  return remainingResponses.reduce(
    (rows, response) => rows.concat(response?.data?.data || []),
    firstRows
  );
};
